import { create } from 'zustand';
import type { Task, DashboardStats } from '@/types';
import { tasksApi, dashboardApi } from '@/lib/api';

interface WbsState {
  tasks: Task[];
  stats: DashboardStats | null;
  managers: string[];
  selectedTasks: Set<number>;
  filters: {
    status: string;
    manager: string;
  };
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchTasks: () => Promise<void>;
  fetchStats: () => Promise<void>;
  fetchManagers: () => Promise<void>;
  refresh: () => Promise<void>;

  createTask: (task: Partial<Task>) => Promise<Task>;
  updateTask: (id: number, task: Partial<Task>) => Promise<Task>;
  deleteTask: (id: number) => Promise<void>;
  bulkDeleteTasks: (ids: number[]) => Promise<void>;
  reorderTasks: (items: Array<{ id: number; order_index: number; parent_id?: number | null; stage_name?: string; depth?: number }>) => Promise<void>;

  setFilter: (key: 'status' | 'manager', value: string) => void;
  toggleTaskSelection: (id: number) => void;
  selectAllTasks: (ids: number[]) => void;
  clearSelection: () => void;
}

export const useWbsStore = create<WbsState>((set, get) => ({
  tasks: [],
  stats: null,
  managers: [],
  selectedTasks: new Set(),
  filters: {
    status: '',
    manager: '',
  },
  isLoading: false,
  error: null,

  fetchTasks: async () => {
    set({ isLoading: true, error: null });
    try {
      const { filters } = get();
      const tasks = await tasksApi.getAll(filters);
      set({ tasks, isLoading: false });
    } catch (error) {
      set({ error: 'Failed to fetch tasks', isLoading: false });
    }
  },

  fetchStats: async () => {
    try {
      const stats = await dashboardApi.getStats();
      set({ stats });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  },

  fetchManagers: async () => {
    try {
      const managers = await dashboardApi.getManagers();
      set({ managers });
    } catch (error) {
      console.error('Failed to fetch managers:', error);
    }
  },

  refresh: async () => {
    const { fetchTasks, fetchStats, fetchManagers } = get();
    await Promise.all([fetchTasks(), fetchStats(), fetchManagers()]);
  },

  createTask: async (task) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const newTask = await tasksApi.create(task as any);
    await get().refresh();
    return newTask;
  },

  updateTask: async (id, task) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updatedTask = await tasksApi.update(id, task as any);
    await get().refresh();
    return updatedTask;
  },

  deleteTask: async (id) => {
    await tasksApi.delete(id);
    await get().refresh();
  },

  bulkDeleteTasks: async (ids) => {
    await tasksApi.bulkDelete(ids);
    set({ selectedTasks: new Set() });
    await get().refresh();
  },

  reorderTasks: async (items) => {
    await tasksApi.reorder(items);
    await get().fetchTasks();
  },

  setFilter: (key, value) => {
    set((state) => ({
      filters: { ...state.filters, [key]: value },
    }));
    get().fetchTasks();
  },

  toggleTaskSelection: (id) => {
    set((state) => {
      const newSelection = new Set(state.selectedTasks);
      if (newSelection.has(id)) {
        newSelection.delete(id);
      } else {
        newSelection.add(id);
      }
      return { selectedTasks: newSelection };
    });
  },

  selectAllTasks: (ids) => {
    set({ selectedTasks: new Set(ids) });
  },

  clearSelection: () => {
    set({ selectedTasks: new Set() });
  },
}));
